// Underpaid-claims topic cluster (hub-and-spoke around the
// /underpaid-insurance-claim-florida pillar landing page).
// Driven by UnderpaidClaimPage.jsx.
//
// FDUTPA-safe: educational framing, hedged coverage language, and any
// statute reference is general information, not legal advice. The theme is
// recovering the *shortfall* on a claim the carrier already paid (or offered)
// but underpaid — distinct from the denied-claims cluster (overturning a full
// denial).

export const underpaidClaims = {
  "underpaid-roof-insurance-claim-florida": {
    slug: "underpaid-roof-insurance-claim-florida",
    peril: "Roof",
    h1: "Underpaid Roof Insurance Claim in Florida?",
    metaTitle:
      "Underpaid Roof Insurance Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Roof claim paid too low in Florida? We recover underpaid roof settlements with test-square inspections, full replacement scoping, and code-upgrade documentation. Free review, no upfront cost.",
    keywords:
      "underpaid roof insurance claim florida, roof claim paid too low, roof claim supplement florida, roof replacement claim underpaid, insurance lowballed roof claim",
    intro:
      "An underpaid roof claim is one of the most common shortfalls we see in Florida. The carrier approves the claim but scopes a repair instead of a replacement, prices shingles below market, or leaves out code-required upgrades, disposal, and steep/high charges. A partial payment is not a final settlement — with a proper test-square inspection and a full replacement-cost estimate, most underpaid roof claims can be supplemented and paid correctly.",
    underpayReasons: [
      "Approving a patch/repair when a full replacement is owed",
      "Below-market shingle, underlayment, and labor pricing",
      "Leaving out code-upgrade (Florida Building Code) requirements",
      "Omitting steep/high, disposal, drip edge, and detach-reset line items",
      "Applying excessive or non-recoverable depreciation",
    ],
    faqs: [
      {
        question: "Why was my roof claim paid so low in Florida?",
        answer:
          "The most common causes are scoping a repair instead of a full replacement, using below-market pricing, and omitting code-upgrade, disposal, and steep/high line items. A test-square inspection and a full Xactimate estimate usually reveal thousands the carrier left out.",
      },
      {
        question: "Can I supplement a roof claim that was already paid?",
        answer:
          "Yes. An initial payment is a starting point, not a ceiling. If the scope or pricing was short, we file a supplement documenting the missing items. Florida sets deadlines to supplement or reopen a claim, so it's worth a free review promptly. (General information, not legal advice.)",
      },
      {
        question: "Does the carrier owe code upgrades on my roof?",
        answer:
          "If your policy includes Ordinance or Law coverage and the repair triggers current Florida Building Code requirements (like re-nailing, secondary water barrier, or full replacement thresholds), those costs are typically owed and frequently left out of the first estimate.",
      },
      {
        question: "What does it cost to have you review an underpaid roof claim?",
        answer:
          "Nothing upfront. We work on contingency within Florida's fee caps and only earn if we recover more for you.",
      },
    ],
    relatedService: { href: "/roof-claim-denial-florida", label: "Roof Claim Help — Full Service Page" },
  },

  "underpaid-water-damage-claim-florida": {
    slug: "underpaid-water-damage-claim-florida",
    peril: "Water Damage",
    h1: "Underpaid Water Damage Claim in Florida?",
    metaTitle:
      "Underpaid Water Damage Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Water damage claim paid too low in Florida? We recover underpaid water claims with moisture mapping, hidden-damage documentation, and full remediation scoping. Free review, no upfront cost.",
    keywords:
      "underpaid water damage claim florida, water claim paid too low, water damage supplement florida, insurance lowballed water claim, water damage claim underpaid",
    intro:
      "Water losses are among the most underpaid claims in Florida because so much of the damage is hidden — behind walls, under floors, and inside cabinets. Carriers often pay for what's visible and stop there, missing subfloor rot, drywall wicking, cabinet delamination, and required mold remediation. We document the full extent of the loss and file a supplement so your payment covers the true cost to dry, demo, and rebuild.",
    underpayReasons: [
      "Paying only for visible damage and missing hidden moisture",
      "Omitting subfloor, drywall, insulation, and cabinet damage",
      "Leaving out required mold remediation and containment",
      "Underscoping demolition, drying equipment, and antimicrobial work",
      "Excessive depreciation on flooring and finishes",
    ],
    faqs: [
      {
        question: "Why is my water damage payout too low?",
        answer:
          "Carriers frequently pay for visible damage only and miss what moisture mapping reveals — wet subfloor, drywall wicking, insulation, and cabinet damage — plus required mold work. We document the full scope and supplement the claim.",
      },
      {
        question: "Can hidden water damage be added after the check?",
        answer:
          "Yes. If damage was missed or discovered during repairs, we document it and file a supplement. A first payment does not close the door on additional covered damage.",
      },
      {
        question: "Is mold remediation part of a water claim?",
        answer:
          "When mold results from a covered water loss, remediation is often covered (subject to policy limits and endorsements). It's commonly left off the first estimate and is a frequent source of underpayment.",
      },
      {
        question: "What will a review cost me?",
        answer:
          "Nothing upfront — contingency only, within Florida's statutory fee caps.",
      },
    ],
    relatedService: { href: "/water-damage", label: "Water Damage Claims — Full Service Page" },
  },

  "underpaid-hurricane-claim-florida": {
    slug: "underpaid-hurricane-claim-florida",
    peril: "Hurricane",
    h1: "Underpaid Hurricane Claim in Florida?",
    metaTitle:
      "Underpaid Hurricane Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Hurricane claim paid too low in Florida? We recover underpaid storm settlements across roof, water, wind, and contents with full-scope documentation. Free review, no upfront cost.",
    keywords:
      "underpaid hurricane claim florida, hurricane claim paid too low, hurricane claim supplement florida, storm claim underpaid, hurricane insurance lowball florida",
    intro:
      "Hurricane claims are complex — roof, water intrusion, wind, screen enclosures, fences, and contents all in one loss — and that complexity is exactly why they get underpaid. Carriers processing thousands of storm claims at once routinely underscope, apply the wrong deductible, or miss whole categories of damage. We reassemble the entire loss into one properly documented, full-scope claim and pursue the shortfall.",
    underpayReasons: [
      "Underscoping across multiple damage categories at once",
      "Missing water intrusion, screen enclosure, and fence damage",
      "Applying the hurricane deductible incorrectly",
      "Below-market pricing during post-storm demand surges",
      "Overlooking contents/personal property and ALE (additional living expenses)",
    ],
    faqs: [
      {
        question: "Why do hurricane claims get underpaid in Florida?",
        answer:
          "Volume and complexity. Carriers handling thousands of storm claims often underscope, miss whole categories (water intrusion, screen cage, fence, contents), or misapply the hurricane deductible. Full-scope documentation recovers what was missed.",
      },
      {
        question: "How long do I have to supplement a hurricane claim in Florida?",
        answer:
          "Florida sets deadlines to file, supplement, and reopen hurricane claims, and those windows have tightened in recent years. The sooner your claim is reviewed, the safer. (General information, not legal advice.)",
      },
      {
        question: "Can you recover my hurricane deductible if it was applied wrong?",
        answer:
          "If the hurricane deductible was applied to a loss it shouldn't have been, we identify and dispute it as part of the supplement.",
      },
      {
        question: "What does it cost?",
        answer:
          "Nothing upfront — contingency only, within Florida's fee caps.",
      },
    ],
    relatedService: { href: "/wind-damage", label: "Hurricane / Wind Damage — Full Service Page" },
  },

  "underpaid-wind-damage-claim-florida": {
    slug: "underpaid-wind-damage-claim-florida",
    peril: "Wind",
    h1: "Underpaid Wind Damage Claim in Florida?",
    metaTitle:
      "Underpaid Wind Damage Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Wind damage claim paid too low in Florida? We recover underpaid wind settlements with uplift documentation and full replacement scoping. Free review, no upfront cost.",
    keywords:
      "underpaid wind damage claim florida, wind claim paid too low, wind damage supplement florida, wind claim underpaid, insurance lowballed wind damage",
    intro:
      "Wind damage is chronically underpaid because so much of it is subtle — lifted shingles, broken seals, hidden uplift, and torn underlayment that don't look dramatic in a quick inspection. Carriers pay for the obvious and miss the rest. We document wind uplift and the full extent of the damage, then supplement the claim so the payment reflects a proper repair or replacement.",
    underpayReasons: [
      "Paying for obvious damage while missing wind uplift and broken seals",
      "Scoping spot repairs instead of a defensible replacement",
      "Omitting underlayment, flashing, and detach-reset items",
      "Below-market labor and material pricing",
      "Excessive depreciation on wind-damaged components",
    ],
    faqs: [
      {
        question: "Why was my wind damage claim underpaid?",
        answer:
          "Wind damage is often subtle — lifted shingles, broken seals, hidden uplift. Quick inspections miss it, so the payment covers only obvious damage. Proper uplift documentation and a full estimate recover the shortfall.",
      },
      {
        question: "Can a wind claim be supplemented after payment?",
        answer:
          "Yes. If the scope or pricing was short, we file a supplement with documentation. A partial payment is not the final number.",
      },
      {
        question: "Is wind damage the same as hurricane damage on my policy?",
        answer:
          "Not always — wind losses outside a named storm may fall under your standard deductible rather than the hurricane deductible. Getting this right can meaningfully change your net recovery.",
      },
      {
        question: "What are your fees?",
        answer:
          "Contingency only, within Florida's statutory caps. No recovery, no fee.",
      },
    ],
    relatedService: { href: "/wind-damage", label: "Wind Damage Claims — Full Service Page" },
  },

  "underpaid-hail-damage-claim-florida": {
    slug: "underpaid-hail-damage-claim-florida",
    peril: "Hail",
    h1: "Underpaid Hail Damage Claim in Florida?",
    metaTitle:
      "Underpaid Hail Damage Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Hail damage claim paid too low in Florida? We recover underpaid hail settlements with test-square bruising documentation and full scoping. Free review, no upfront cost.",
    keywords:
      "underpaid hail damage claim florida, hail claim paid too low, hail damage supplement florida, hail claim underpaid, insurance lowballed hail damage",
    intro:
      "Hail damage is one of the most underpaid perils in Florida because bruising and fractured mats aren't visible from the ground. Carriers frequently call verified hail damage 'cosmetic' or pay for a handful of shingles when the roof qualifies for replacement. A proper test-square inspection documenting bruising and granule loss is what moves a lowballed hail payment to a full one.",
    underpayReasons: [
      "Calling functional hail damage 'cosmetic'",
      "Paying for spot repairs instead of qualifying replacement",
      "Missing bruised mats, fractured shingles, and granule loss",
      "Omitting soft-metal and gutter/downspout hail damage",
      "Below-market pricing and excessive depreciation",
    ],
    faqs: [
      {
        question: "Why do carriers underpay hail claims?",
        answer:
          "Hail bruising and fractured mats aren't visible from the ground, so quick inspections underscope or label damage 'cosmetic.' A test-square inspection documenting functional damage supports a full replacement.",
      },
      {
        question: "What is 'cosmetic' hail damage?",
        answer:
          "Carriers use 'cosmetic' to argue the roof still sheds water. 'Functional' means the shingle's integrity is compromised. Documenting bruised mats and granule loss frequently proves functional damage owed at replacement cost.",
      },
      {
        question: "Can I supplement a hail claim that was paid?",
        answer:
          "Yes. If the roof qualified for more than the carrier paid, we document it and file a supplement within Florida's deadlines.",
      },
      {
        question: "What does a review cost?",
        answer:
          "Nothing upfront — contingency only, within Florida's fee caps.",
      },
    ],
    relatedService: { href: "/hail-damage", label: "Hail Damage Claims — Full Service Page" },
  },

  "underpaid-fire-damage-claim-florida": {
    slug: "underpaid-fire-damage-claim-florida",
    peril: "Fire & Smoke",
    h1: "Underpaid Fire Damage Claim in Florida?",
    metaTitle:
      "Underpaid Fire & Smoke Damage Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Fire or smoke claim paid too low in Florida? We recover underpaid fire settlements with full structure, contents, smoke, and ALE documentation. Free review, no upfront cost.",
    keywords:
      "underpaid fire damage claim florida, fire claim paid too low, smoke damage claim underpaid, fire claim supplement florida, insurance lowballed fire claim",
    intro:
      "Fire claims are large and multi-layered — structure, smoke, soot, contents, odor, and additional living expenses — and every layer is a place the carrier can underpay. Smoke and odor remediation are especially undervalued, and contents are often paid at depreciated value without the recoverable holdback. We document the entire loss, including hidden smoke penetration and full replacement-cost contents, and pursue the shortfall.",
    underpayReasons: [
      "Undervaluing smoke, soot, and odor remediation",
      "Paying contents at depreciated value without recoverable holdback",
      "Missing hidden smoke penetration in HVAC, walls, and insulation",
      "Underscoping structural demolition and rebuild",
      "Overlooking additional living expenses (ALE)",
    ],
    faqs: [
      {
        question: "Why was my fire claim underpaid?",
        answer:
          "Fire claims have many components, and smoke/odor remediation and contents are frequently undervalued. We document hidden smoke penetration and full replacement-cost contents to recover the shortfall.",
      },
      {
        question: "Should smoke and odor be a separate part of my claim?",
        answer:
          "Yes. Smoke penetrates HVAC, walls, and insulation and requires specialized remediation that carriers often underscope or omit. It's a common source of underpayment.",
      },
      {
        question: "Can I recover depreciation on my burned contents?",
        answer:
          "Under replacement-cost policies, recoverable depreciation (holdback) is generally owed once items are replaced. It's often left unpaid unless it's properly claimed.",
      },
      {
        question: "What are your fees?",
        answer:
          "Contingency only, within Florida's statutory caps.",
      },
    ],
    relatedService: { href: "/fire-damage", label: "Fire Damage Claims — Full Service Page" },
  },

  "underpaid-mold-claim-florida": {
    slug: "underpaid-mold-claim-florida",
    peril: "Mold",
    h1: "Underpaid Mold Claim in Florida?",
    metaTitle:
      "Underpaid Mold Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Mold claim paid too low in Florida? We recover underpaid mold settlements with full containment, remediation, and reconstruction scoping. Free review, no upfront cost.",
    keywords:
      "underpaid mold claim florida, mold claim paid too low, mold remediation claim underpaid, mold claim supplement florida, insurance lowballed mold claim",
    intro:
      "Mold claims are underpaid in two directions at once: carriers apply a low mold sublimit and then underscope the remediation and the reconstruction that follows it. Proper mold work is containment, HEPA filtration, removal, antimicrobial treatment, clearance testing, and rebuild — not a quick wipe-down. We document the full protocol and the underlying water cause so the payment reflects the real cost of doing it right.",
    underpayReasons: [
      "Applying a low mold sublimit without scoping the full remediation",
      "Omitting containment, HEPA filtration, and clearance testing",
      "Underscoping the reconstruction after remediation",
      "Disputing the covered water cause behind the mold",
      "Excessive depreciation on removed materials",
    ],
    faqs: [
      {
        question: "Why is my mold payout so low?",
        answer:
          "Mold claims are often capped by a sublimit and then underscoped on top of that. Full remediation — containment, HEPA, removal, antimicrobial, clearance testing — plus reconstruction is frequently left incomplete on the first estimate.",
      },
      {
        question: "Is mold covered by my Florida policy?",
        answer:
          "Mold resulting from a covered water loss is often covered up to a policy sublimit, which varies by policy and endorsement. We document the covered cause and the full remediation protocol.",
      },
      {
        question: "Can I supplement a mold claim?",
        answer:
          "Yes. If remediation or reconstruction was underscoped, we file a supplement documenting the full protocol within Florida's deadlines.",
      },
      {
        question: "What does a review cost?",
        answer:
          "Nothing upfront — contingency only, within Florida's fee caps.",
      },
    ],
    relatedService: { href: "/mold-damage", label: "Mold Damage Claims — Full Service Page" },
  },

  "underpaid-contents-personal-property-claim-florida": {
    slug: "underpaid-contents-personal-property-claim-florida",
    peril: "Contents & Personal Property",
    h1: "Underpaid Contents / Personal Property Claim in Florida?",
    metaTitle:
      "Underpaid Contents & Personal Property Claim Florida | Recover the Shortfall | Claim Beast",
    metaDescription:
      "Personal property claim paid too low in Florida? We recover underpaid contents with proper replacement-cost valuation and depreciation holdback. Free review, no upfront cost.",
    keywords:
      "underpaid contents claim florida, personal property claim paid too low, contents claim underpaid, replacement cost contents florida, insurance lowballed contents claim",
    intro:
      "Contents and personal-property payments are almost always the most underpaid part of a claim. Carriers apply steep depreciation, use low replacement pricing, and fail to pay the recoverable holdback once items are actually replaced. Building a proper, itemized inventory at true replacement cost — with the right ages, conditions, and like-kind-and-quality pricing — routinely increases the contents recovery by a meaningful margin.",
    underpayReasons: [
      "Excessive depreciation applied to personal property",
      "Below-market or non-like-kind replacement pricing",
      "Failing to pay recoverable depreciation (holdback) after replacement",
      "Incomplete or under-inventoried contents lists",
      "Overlooking sales tax and high-value/scheduled items",
    ],
    faqs: [
      {
        question: "Why is my contents payment so low?",
        answer:
          "Carriers apply heavy depreciation and low replacement pricing, and often don't pay the recoverable holdback. A proper itemized inventory at true replacement cost recovers most of the difference.",
      },
      {
        question: "What is recoverable depreciation on contents?",
        answer:
          "Under replacement-cost coverage, the carrier withholds depreciation initially and owes it once you replace the items and submit proof. This holdback is frequently left unclaimed.",
      },
      {
        question: "Do I have to list every item?",
        answer:
          "A thorough, itemized inventory drives the recovery. We help build it with ages, conditions, and like-kind-and-quality pricing so nothing is left on the table.",
      },
      {
        question: "What are your fees?",
        answer:
          "Contingency only, within Florida's statutory caps. No recovery, no fee.",
      },
    ],
  },
};
